// Typed ts-rest client. Built against the shared contract package.
// Always sends cookies so the session middleware on the server can mint/read
// the `septima-session` cookie.
//
// Base URL selection (single source of truth — `baseUrl` below):
//   - Local dev: VITE_API_BASE is empty, so baseUrl === '' and every request
//     is relative (`/api/...`). The Vite dev server's same-origin proxy
//     forwards /api to the Nest server (see vite.config.ts).
//   - Production (S3/CloudFront SPA → ALB API): VITE_API_BASE is the absolute
//     API origin (e.g. https://api.septima.example.com), baked in at BUILD
//     time. Requests then go cross-origin to that host.
// Because cross-origin requests carry cookies (`credentials: 'include'`), the
// server must set the session cookie `SameSite=None; Secure` in production so
// the browser will send it back to the API origin (the server already does).

import { initClient } from '@ts-rest/core';
import { apiContract } from '@septima/contract';
import type { StaffActionResult } from '@septima/contract';

const baseUrl = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export const api = initClient(apiContract, {
    baseUrl,
    baseHeaders: {},
    credentials: 'include',
});

// Bridge wrapper around the typed `{status, body}` envelope returned by
// per-area ts-rest calls. Normalises into the `StaffActionResult` shape the
// pages and the typed-dispatcher helpers consume — a single
// `if (result.ok) ...` glue point for every mutation that supersedes the
// legacy named-action surface (OLD_CODE.html #2).
//
// Treats any non-200 status as `{ok: false, error: <message>}`; preserves
// the typed 200 body verbatim. Endpoint 4xx bodies (`{ok: false, error}`)
// flow through directly.
type EnvelopeStatus = { status: number; body: unknown };
export async function runStaffEndpoint(call: Promise<EnvelopeStatus>): Promise<StaffActionResult> {
    const res = await call;
    const body = res.body as Record<string, unknown> | undefined;
    if (res.status === 200) {
        return (body ?? { ok: true }) as StaffActionResult;
    }
    const err =
        body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
            ? body.error
            : `Request failed (status ${res.status})`;
    return { ok: false, error: err };
}

// Raw-fetch escape hatch for endpoints the ts-rest client doesn't cover
// (Outlook OAuth, auth/session). Mirrors the baseUrl + credentials behaviour
// of the typed client so callers don't have to remember either.
export function apiUrl(path: string): string {
    if (!path.startsWith('/')) return path;
    return `${baseUrl}${path}`;
}

export function apiFetch(path: string, init?: RequestInit): Promise<Response> {
    return window.fetch(apiUrl(path), { credentials: 'include', ...init });
}
